import { waitWithTimeout } from "./waitWithTimeout";

export type AllSettledResults<T = unknown> =
  | PromiseSettledResult<T>[]
  | PromiseSettledResult<T>[][];

/**
 * Logs errors from all settled promises to the console.
 * This function filters the results to find all rejected promises and logs their reasons.
 * It also logs a summary message indicating how many promises were rejected.
 *
 * @param allSettledResults The results of the promises settled using Promise.allSettled.
 * @param logMessagePrefix Optional prefix for the log messages that will be
 * prepended to the error message if any promises are rejected.
 */
export function logAllSettledPromiseErrors(
  allSettledResults: AllSettledResults = [],
  logMessagePrefix?: string,
) {
  const rejected = allSettledResults
    .flat()
    .filter((result) => result.status === "rejected");
  if (rejected.length > 0) {
    for (const rejectedResult of rejected) {
      console.warn(rejectedResult.reason);
    }

    let errorMessage = `${rejected.length} of ${allSettledResults.length} promises were rejected.`;
    if (logMessagePrefix) {
      errorMessage = `${logMessagePrefix} ${errorMessage}`;
    }

    console.warn(errorMessage, rejected);
  }
}

/**
 * Waits for all promises to settle (using Promise.allSettled) and logs any
 * rejected promises to the console using `console.warn`. This will throw an
 * error if the provided timeout value is exceeded before all promises settle.
 *
 * @param promises The array of promises to wait.
 * @param timeout The time in milliseconds to wait for all promises to settle
 * before throwing a timeout error
 * @param logMessagePrefix Optional prefix for the log messages that will be
 * prepended to the error message if any promises are rejected.
 * @returns A promise that resolves with an array of PromiseSettledResult objects.
 * @throws Error if the timeout is exceeded before all promises settle.
 */
export async function waitForAllSettled<PromiseResult = unknown>(
  promises: Promise<PromiseResult>[],
  timeout: number,
  logMessagePrefix?: string,
): Promise<AllSettledResults<PromiseResult>> {
  const results = await waitWithTimeout(
    Promise.allSettled(promises),
    timeout,
    logMessagePrefix,
  );
  logAllSettledPromiseErrors(results, logMessagePrefix);
  return results;
}
